import React from 'react';
import type {
  AfcSubmissionCandidateReputation,
  CandidateReputation,
} from '@axiom/validation';

import { Layout } from '../../layout/Layout/Layout';
import { Well } from '../../layout/Well/Well';
import { Grid } from '../../layout/Grid/Grid';
import { GridRow } from '../../layout/Grid/GridRow';
import { GridColumn } from '../../layout/Grid/GridColumn';
import { ParagraphHeader } from '../../content/ParagraphHeader/ParagraphHeader';
import { Paragraph } from '../../content/Paragraph/Paragraph';
import { CondensedMedium } from '../../content/CondensedMedium/CondensedMedium';
import { ProgressGroup } from '../../element/ProgressGroup/ProgressGroup';
import { ProgressGroupItem } from '../../element/ProgressGroup/ProgressGroupItem';
import { Progress } from '../../element/Progress/Progress';
import { ProgressGroupUtil } from '../../element/ProgressGroup/progress-group-util';
import { CondensedSmall } from '../../content/CondensedSmall/CondensedSmall';
import { Flashy } from '../../element/Flashy/Flashy';
import { Gutter } from '../../layout/Gutter/Gutter';
import { PluralizeUtil } from '../../../utils/pluralize-util';

export type CandidatePerformanceFeedbackProps = {
  candidateReputation:
    | CandidateReputation
    | AfcSubmissionCandidateReputation
    | null;
  stacked?: boolean;
};
const totalSteps = 4;

const getProgressColor = (score?: number | null) => {
  if (typeof score === 'number') {
    return ProgressGroupUtil.lessThanHalf(score - 1, totalSteps)
      ? 'matcha'
      : 'forest green';
  }

  return 'gray';
};

const formatSummaryBlurb = (str: string) => {
  const [first, second, ...rest] = str.split(' ');

  return (
    <>
      <ParagraphHeader as="span" name="bold-block">
        {[first, second].join(' ')}{' '}
      </ParagraphHeader>
      <Paragraph as="span" name="non-bold-block">
        {rest.join(' ')}
      </Paragraph>
    </>
  );
};

export const CandidatePerformanceFeedback = ({
  candidateReputation,
  stacked = false,
}: CandidatePerformanceFeedbackProps) => {
  const resolvedCandidateReputation =
    candidateReputation === null
      ? ({
          businessJudgementScore: null,
          communicationScore: null,
          createdAt: null,
          displayableSurveyScoreData: false,
          displayCandidateReputation: false,
          hasEnoughRespondents: false,
          legalExpertiseScore: null,
          numberOfAxiomEngagements: 0,
          numberOfEngagements: 0,
          numberOfRespondents: 0,
          numberOfReviews: 0,
          numberOfTestimonials: 0,
          percentageOfReEngagements: null,
          reputationSummary: null,
          responsivenessScore: null,
          updatedAt: null,
          yearsAtAxiom: 0,
        } as CandidateReputation)
      : candidateReputation;

  return (
    <Layout
      name="CandidateReputation"
      verticalGutter="16px"
      direction="vertical"
    >
      <Well background="light blue">
        <Grid name="CandidatePerformanceGrid">
          <GridRow>
            <GridColumn name="PerformanceFeedbackSection">
              <Well background="light">
                <Layout verticalGutter="16px" direction="vertical">
                  <ParagraphHeader>Performance Feedback</ParagraphHeader>
                  <ProgressGroup stacked={stacked}>
                    {resolvedCandidateReputation.communicationScore !==
                      null && (
                      <ProgressGroupItem
                        label={
                          <CondensedSmall>
                            <Flashy bold>Communication</Flashy>
                          </CondensedSmall>
                        }
                      >
                        <Progress
                          name="CommunicationProgress"
                          color={getProgressColor(
                            resolvedCandidateReputation.communicationScore
                          )}
                          currentStep={Math.max(
                            resolvedCandidateReputation.communicationScore - 1,
                            0
                          )}
                          totalSteps={totalSteps}
                        />
                      </ProgressGroupItem>
                    )}
                    {resolvedCandidateReputation.legalExpertiseScore !==
                      null && (
                      <ProgressGroupItem
                        label={
                          <CondensedSmall>
                            <Flashy bold>Legal Acumen</Flashy>
                          </CondensedSmall>
                        }
                      >
                        <Progress
                          name="LegalExpertiseProgress"
                          color={getProgressColor(
                            resolvedCandidateReputation.legalExpertiseScore
                          )}
                          currentStep={Math.max(
                            resolvedCandidateReputation.legalExpertiseScore - 1,
                            0
                          )}
                          totalSteps={totalSteps}
                        />
                      </ProgressGroupItem>
                    )}
                    {resolvedCandidateReputation.businessJudgementScore !==
                      null && (
                      <ProgressGroupItem
                        label={
                          <CondensedSmall>
                            <Flashy bold>Business Judgement</Flashy>
                          </CondensedSmall>
                        }
                      >
                        <Progress
                          name="BusinessJudgementProgress"
                          color={getProgressColor(
                            resolvedCandidateReputation.businessJudgementScore
                          )}
                          currentStep={Math.max(
                            resolvedCandidateReputation.businessJudgementScore -
                              1,
                            0
                          )}
                          totalSteps={totalSteps}
                        />
                      </ProgressGroupItem>
                    )}
                    {resolvedCandidateReputation.responsivenessScore !==
                      null && (
                      <ProgressGroupItem
                        label={
                          <CondensedSmall>
                            <Flashy bold>Responsiveness</Flashy>
                          </CondensedSmall>
                        }
                      >
                        <Progress
                          name="ResponsivenessProgress"
                          color={getProgressColor(
                            resolvedCandidateReputation.responsivenessScore
                          )}
                          currentStep={Math.max(
                            resolvedCandidateReputation.responsivenessScore - 1,
                            0
                          )}
                          totalSteps={totalSteps}
                        />
                      </ProgressGroupItem>
                    )}
                  </ProgressGroup>
                </Layout>
              </Well>
              <Gutter
                name="PerformanceFeedbackSectionGutter"
                bottom={
                  resolvedCandidateReputation.reputationSummary ? '16px' : null
                }
              />
            </GridColumn>
          </GridRow>
          {resolvedCandidateReputation.reputationSummary ? (
            <GridRow>
              <GridColumn name="ReputationSummarySection">
                {formatSummaryBlurb(
                  resolvedCandidateReputation.reputationSummary
                )}
              </GridColumn>
            </GridRow>
          ) : null}
        </Grid>
      </Well>
      <CondensedMedium name="FeedbackAiVerbiage">
        Performance feedback and AI summary are based on{' '}
        {resolvedCandidateReputation.numberOfReviews}{' '}
        {PluralizeUtil({
          text: 'review',
          count: resolvedCandidateReputation.numberOfReviews,
        })}{' '}
        and {resolvedCandidateReputation.numberOfTestimonials}{' '}
        {PluralizeUtil({
          text: 'testimonial',
          count: resolvedCandidateReputation.numberOfTestimonials,
        })}{' '}
        from {resolvedCandidateReputation.numberOfEngagements} out of{' '}
        {resolvedCandidateReputation.numberOfAxiomEngagements}{' '}
        {PluralizeUtil({
          text: 'engagement',
          count: resolvedCandidateReputation.numberOfAxiomEngagements,
        })}
        . Ratings are based on individual client experiences, which can vary by
        project type and complexity. Some clients may choose not to share
        feedback publicly. Consider them as initial indicators rather than
        definitive performance measures.
      </CondensedMedium>
    </Layout>
  );
};
